/**
 * Request Access form - populates Relationship to Resideo select from API
 */
import { getCustomerAppBaseUrl } from "@theme/gadget-envs";

(function () {
  'use strict';

  const resideoEmployeeLabel = /^resideo employee$/i;
  const resideoEmailDomain = /@resideo\.com$/i;

  function isResideoEmail(email) {
    return resideoEmailDomain.test(String(email || '').trim());
  }

  function getOptionValue(item) {
    return item.id ?? item.value ?? item.name ?? String(item);
  }

  function getOptionLabel(item) {
    if (typeof item === 'string') return item;
    return item.name ?? item.label ?? item.title ?? String(item.id ?? item.value ?? item);
  }

  function parseRelationshipsResponse(data) {
    const list = Array.isArray(data) ? data : data?.data ?? data?.relationships ?? [];
    return list.map((item) => ({
      value: getOptionValue(item),
      label: getOptionLabel(item),
      separatorBefore: item.separatorBefore === true,
    }));
  }

  function createSeparatorElement() {
    const li = document.createElement('li');
    li.className = 'rs-custom-select__option-separator';
    li.setAttribute('role', 'presentation');
    li.setAttribute('aria-hidden', 'true');
    return li;
  }

  function createOptionElement(value, label) {
    const li = document.createElement('li');
    li.className = 'rs-custom-select__option';
    li.setAttribute('role', 'option');
    li.setAttribute('tabindex', '0');
    li.setAttribute('data-value', String(value));
    li.setAttribute('aria-selected', 'false');
    li.setAttribute('aria-label', label);
    const span = document.createElement('span');
    span.className = 'rs-custom-select__option-label';
    span.textContent = label;
    li.appendChild(span);
    return li;
  }

  async function loadRelationships() {
    const container = document.querySelector('[data-relationships-select-container]');
    if (!container) return;

    const loadingEl = container.querySelector('[data-relationships-loading]');
    const wrapperEl = container.querySelector('[data-relationships-select-wrapper]');
    const errorEl = container.querySelector('[data-relationships-error]');

    if (!loadingEl || !wrapperEl) return;

    try {
      loadingEl.classList.remove('rs-hidden');
      wrapperEl.classList.add('rs-hidden');
      if (errorEl) {
        errorEl.classList.add('rs-hidden');
        errorEl.textContent = '';
      }

      const res = await fetch(`${getCustomerAppBaseUrl()}/api/customer/relationships-to-resideo`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.myJWT}`
        }
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.message ?? data?.error ?? `Failed to load options (${res.status})`);
      }

      const items = parseRelationshipsResponse(data);
      const selectWrapper = wrapperEl.querySelector('.rs-custom-select__wrapper');
      const menu = selectWrapper?.querySelector('.rs-custom-select__menu');
      if (!menu) {
        throw new Error('Select menu not found');
      }

      menu.innerHTML = '';
      items.forEach((item) => {
        if (item.separatorBefore || /^resideo employee$/i.test(item.label)) {
          menu.appendChild(createSeparatorElement());
        }
        menu.appendChild(createOptionElement(item.value, item.label));
      });

      loadingEl.classList.add('rs-hidden');
      wrapperEl.classList.remove('rs-hidden');

      if (items.length > 0 && selectWrapper && typeof window.initCustomSelect === 'function') {
        const selectEl = selectWrapper.querySelector('.rs-custom-select');
        if (selectEl) {
          delete selectEl.dataset.initialized;
        }
        window.initCustomSelect(selectWrapper);
      }
    } catch (err) {
      loadingEl.classList.add('rs-hidden');
      wrapperEl.classList.remove('rs-hidden');
      if (errorEl) {
        errorEl.textContent = err.message || 'Unable to load options. Please try again.';
        errorEl.classList.remove('rs-hidden');
      }
    }
  }

  function showFieldError(wrapper) {
    if (!wrapper) return;
    wrapper.classList.add('rs-error');
  }

  function clearFieldError(wrapper) {
    if (!wrapper) return;
    wrapper.classList.remove('rs-error');
  }

  function clearAllValidationErrors() {
    document.querySelectorAll('[data-request-access-validate]').forEach((el) => {
      const wrapper = el.querySelector('.rs-input__content, .rs-custom-select__wrapper');
      if (wrapper) clearFieldError(wrapper);
    });
    const relationshipWrapper = document.querySelector('[data-relationships-select-container] .rs-custom-select__wrapper');
    if (relationshipWrapper) clearFieldError(relationshipWrapper);
  }

  function getFieldValue(form, fieldName) {
    const input = form.querySelector(`input[name="contact[${fieldName}]"]`);
    return input ? String(input.value || '').trim() : '';
  }

  function validateForm(form) {
    clearAllValidationErrors();
    let isValid = true;
    const isResideoEmployee = resideoEmployeeLabel.test(
      String(form.querySelector('[data-relationships-select-container] .rs-custom-select__option.is-selected .rs-custom-select__option-label')?.textContent?.trim() || '')
    );

    const requiredFields = [
      { name: 'relationship', label: 'Relationship to Resideo', conditional: false },
      { name: 'businessName', label: 'Business Name', conditional: true },
      { name: 'addressLine1', label: 'Address Line 1', conditional: true },
      { name: 'city', label: 'City', conditional: true },
      { name: 'state', label: 'State', conditional: true },
      { name: 'zipCode', label: 'ZIP Code', conditional: true },
      { name: 'country', label: 'Country', conditional: true },
      { name: 'businessPhoneNumber', label: 'Business Phone Number', conditional: true },
    ];

    requiredFields.forEach((field) => {
      if (field.conditional && isResideoEmployee) return;

      const validateEl = field.name === 'relationship'
        ? document.querySelector('[data-relationships-select-container]')
        : document.querySelector(`[data-request-access-validate="${field.name}"]`);
      if (!validateEl) return;

      if (validateEl.closest('.rs-hidden') || validateEl.closest('[data-request-access-conditional-fields].rs-hidden')) return;

      const value = field.name === 'relationship'
        ? getFieldValue(form, 'relationship')
        : getFieldValue(form, field.name);

      if (!value) {
        isValid = false;
        const wrapper = field.name === 'relationship'
          ? validateEl.querySelector('.rs-custom-select__wrapper')
          : validateEl.querySelector('.rs-input__content, .rs-custom-select__wrapper');
        showFieldError(wrapper);
      }
    });

    return isValid;
  }

  function getFormValue(fd, ...keys) {
    for (const key of keys) {
      const val = fd.get(`contact[${key}]`);
      if (val !== null && val !== undefined && String(val).trim() !== '') return String(val).trim();
    }
    return '';
  }

  function formDataToObject(form) {
    const fd = new FormData(form);

    const relationshipToResideo = getFormValue(fd, 'relationship') || '';
    const otherRelationshipToResideo = getFormValue(fd, 'otherRelationshipToResideo', 'other_relationship');
    const sapAccountNumber = getFormValue(fd, 'sapAccountNumber', 'sap_account');
    const contactSap = fd.get('contact[contactMeToAddMoreAccounts]') ?? fd.get('contact[contact_sap]');
    const contactMeToAddMoreAccounts = contactSap === 'yes' || contactSap === 'on';

    const businessAddress = {
      businessName: getFormValue(fd, 'businessName', 'business_name') || '',
      addressLine1: getFormValue(fd, 'addressLine1', 'address1') || '',
      addressLine2: getFormValue(fd, 'addressLine2', 'address2'),
      city: getFormValue(fd, 'city') || '',
      state: getFormValue(fd, 'state') || '',
      zipCode: getFormValue(fd, 'zipCode', 'zip') || '',
      country: getFormValue(fd, 'country') || '',
    };

    const businessPhoneNumber = getFormValue(fd, 'businessPhoneNumber', 'phone') || '';
    const cellPhoneNumber = getFormValue(fd, 'cellPhoneNumber', 'cell_phone');
    const faxNumber = getFormValue(fd, 'faxNumber', 'fax');

    const payload = {
      relationshipToResideo,
      businessAddress,
      businessPhoneNumber,
    };

    if (otherRelationshipToResideo) payload.otherRelationshipToResideo = otherRelationshipToResideo;
    if (sapAccountNumber) payload.sapAccountNumber = sapAccountNumber;
    if (contactMeToAddMoreAccounts) payload.contactMeToAddMoreAccounts = true;
    if (businessAddress.addressLine2) payload.businessAddress.addressLine2 = businessAddress.addressLine2;
    else delete payload.businessAddress.addressLine2;
    if (cellPhoneNumber) payload.cellPhoneNumber = cellPhoneNumber;
    if (faxNumber) payload.faxNumber = faxNumber;

    return payload;
  }

  async function submitAccessRequest(form) {

    if (!validateForm(form)) {
      const firstError = form.querySelector('.rs-error');
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const relationshipLabel = form.querySelector('[data-relationships-select-container] .rs-custom-select__option.is-selected .rs-custom-select__option-label')?.textContent?.trim();
    if (resideoEmployeeLabel.test(String(relationshipLabel || ''))) {
      const email = form.querySelector('input[name="contact[email]"]')?.value ?? '';
      if (!isResideoEmail(email)) {
        showResideoEmailError(true);
        return;
      }
    }

    const submitBtn = document.querySelector('.request-access-form__submit');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting...';
    }

    try {
      const payload = formDataToObject(form);
      const url = `${getCustomerAppBaseUrl()}/api/customer/access-request?customerId=gid://shopify/Customer/${document.body.getAttribute('data-customer-id')}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${window.myJWT}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.message ?? data?.error ?? `Request failed (${res.status})`);
      }

      window.location = document.cookie.includes('redirect_url') ? document.cookie.split('redirect_url=')[1].split(';')[0] : '/';
    } catch {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText || 'Submit Request';
      }
    }
  }

  function showResideoEmailError(show) {
    const errorEl = document.querySelector('[data-resideo-email-error]');
    const selectContainer = document.querySelector('[data-relationships-select-container]');
    const selectWrapper = selectContainer?.querySelector('.rs-custom-select__wrapper');
    if (errorEl) {
      errorEl.classList.toggle('rs-hidden', !show);
    }
    if (selectWrapper) {
      selectWrapper.classList.toggle('rs-error', show);

      if (show) {
        selectContainer?.style?.setProperty('margin-bottom', '0');
      } else {
        selectContainer?.removeAttribute('style');
      }
    }
  }

  function toggleConditionalFields(relationshipLabel) {
    const form = document.getElementById('request-access-form');
    const conditionalEls = document.querySelectorAll('[data-request-access-conditional-fields]');
    const isResideoEmployee = resideoEmployeeLabel.test(String(relationshipLabel || '').trim());
    const submitBtn = document.querySelector('.request-access-form__submit');

    conditionalEls.forEach((el) => {
      el.classList.toggle('rs-hidden', isResideoEmployee);
      const inputs = el.querySelectorAll('input, select');
      inputs.forEach((input) => {
        if (isResideoEmployee) {
          input.dataset.wasRequired = input.hasAttribute('required') ? 'true' : '';
          input.removeAttribute('required');
        } else if (input.dataset.wasRequired === 'true') {
          input.setAttribute('required', '');
          delete input.dataset.wasRequired;
        }
      });
    });

    if (isResideoEmployee && form) {
      const emailInput = form.querySelector('input[name="contact[email]"]');
      const email = emailInput?.value ?? '';
      if (!isResideoEmail(email)) {
        showResideoEmailError(true);
        if (submitBtn) submitBtn.disabled = true;
      } else {
        showResideoEmailError(false);
        if (submitBtn) submitBtn.disabled = false;
      }
    } else {
      showResideoEmailError(false);
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  function initRelationshipToggle() {
    const form = document.getElementById('request-access-form');
    const container = document.querySelector('[data-relationships-select-container]');
    if (!form || !container) return;

    form.addEventListener('change', (e) => {
      if (!container.contains(e.target)) return;
      const selectWrapper = container.querySelector('.rs-custom-select__wrapper');
      if (!selectWrapper || !selectWrapper.contains(e.target)) return;
      const label = e.detail?.label ?? selectWrapper.querySelector('.rs-custom-select__option.is-selected .rs-custom-select__option-label')?.textContent?.trim() ?? '';
      toggleConditionalFields(label);
    });
  }

  function initStateOptionsByCountry() {
    const form = document.getElementById('request-access-form');
    const countrySelect = document.getElementById('rs-custom-select-request-access-country');
    const stateSelect = document.getElementById('rs-custom-select-request-access-state');

    const countryInput = countrySelect?.querySelector('input[type="hidden"]');
    const stateInput = stateSelect?.querySelector('input[type="hidden"]');
    const stateMenu = stateSelect?.querySelector('.rs-custom-select__menu');
    const stateButton = stateSelect?.querySelector('.rs-custom-select__button');
    const stateValue = stateButton?.querySelector('.rs-custom-select__value');

    if (!form || !countrySelect || !stateSelect ||
      !countryInput || !stateInput || !stateMenu || !stateValue) {
      return;
    }

    const stateOptions = Array.from(
      stateMenu.querySelectorAll('.rs-custom-select__option[data-country]')
    );

    // Preserve the original placeholder.
    const placeholder = stateValue.textContent.trim();

    function updateStateOptions(resetSelection = false) {
      const country = countryInput.value.trim().toUpperCase();
      const stateCountry = ['US', 'CA'].includes(country) ? country : 'OTHER';

      stateOptions.forEach((option) => {
        const isVisible = option.dataset.country === stateCountry;

        option.hidden = !isVisible;
        option.style.display = isVisible ? '' : 'none';

        option.classList.remove('is-selected', 'is-active');
        option.setAttribute('aria-selected', 'false');
        option.setAttribute('tabindex', isVisible ? '0' : '-1');
      });

      if (resetSelection) {
        stateInput.value = '';
        stateValue.textContent = placeholder;
        stateValue.classList.add('rs-custom-select__value--placeholder');

        stateButton.setAttribute('aria-expanded', 'false');
        stateMenu.hidden = false;
      }
    }

    // Set the correct options for the initially selected country.
    updateStateOptions();

    // Update the state list whenever the country changes.
    form.addEventListener('change', (event) => {
      if (
        event.target === countryInput ||
        countrySelect.contains(event.target)
      ) {
        updateStateOptions(true);
      }
    });
  }

  function initValidationListeners(form) {
    form.addEventListener('input', (e) => {
      const target = e.target;
      if (!target.matches('input:not([type="hidden"])')) return;
      const wrapper = target.closest('.rs-input__content');
      if (wrapper?.classList.contains('rs-error') && String(target.value || '').trim()) {
        clearFieldError(wrapper);
      }
    });
    form.addEventListener('change', (e) => {
      const wrapper = e.target.closest('.rs-custom-select__wrapper');
      if (wrapper?.classList.contains('rs-error')) {
        const hiddenInput = wrapper.querySelector('input[type="hidden"]');
        if (hiddenInput?.value?.trim()) clearFieldError(wrapper);
      }
    });
  }

  function initFormSubmit() {
    const form = document.getElementById('request-access-form');
    const submitBtn = document.querySelector('.request-access-form__submit');

    if (submitBtn) {
      submitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        submitAccessRequest(form);
      });
    }
    initValidationListeners(form);
  }

  function checkFirstNameLastName() {
    const firstName = document.querySelector('input[name="contact[first_name]"]')?.value ?? '';
    const lastName = document.querySelector('input[name="contact[last_name]"]')?.value ?? '';
    if (firstName.trim() === '' || lastName.trim() === '') {
      showMissingFirstNameLastNameBanner();
      return;
    }
  }

  function showMissingFirstNameLastNameBanner() {
    const banner = document.querySelector('.rs-banner--info');
    if (banner) banner.classList.add('rs-banner--visible');
  }

  function init(attempts = 0) {
    if (!document.querySelector('[data-relationships-select-container]')) return;
    if (window.myJWT !== undefined) {
      checkFirstNameLastName();
      loadRelationships();
    } else if (attempts < 50) {
      setTimeout(() => init(attempts + 1), 100);
    } else {
      loadRelationships();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      initFormSubmit();
      initRelationshipToggle();
      initStateOptionsByCountry();
    });
  } else {
    init();
    initFormSubmit();
    initRelationshipToggle();
    initStateOptionsByCountry();
  }
})();
